import { useState, useEffect, useRef } from 'react';
import { X, Search, ArrowUpDown, ChevronDown, Edit2, Save, Plus, ChevronRight, Check, Trash2, RefreshCw, Copy, Ellipsis } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { updateTemplate, addTemplate, setSelectedTemplate, deleteTemplate, clearSelectedTemplate, fetchTemplates, setTemplateSelectCallback, clearTemplateSelectCallback } from '../../store/reducers';
import { templateColorPalette } from '../../utils/templateColors';
import toast from 'react-hot-toast';
import {
  getTemplates,
  formatFieldName,
  organizeTemplateFields,
  generateNewTemplateId,
  generateTemplateId,
  marcasToString,
  stringToMarcas,
  getInitialValue,
  getExistingFields,
  getExistingFieldsForDocument,
  getAvailableFields,
  addFieldsToTemplateData,
  convertFieldValue
} from '../../utils/templateUtils';
import { deepClone } from '../../utils/dataHelpers';

// Re-export for backward compatibility
export { getTemplates };

export const TemplateModal = ({
  templates: templatesProp,
  isOpen,
  onClose,
  onSelect = null,
  selectedTemplate,
  title,
  isSpanish,
}) => {
  const dispatch = useDispatch();
  
  // Get templates and loading state from Redux if not provided as prop
  const reduxTemplates = useSelector((state) => {
    const templates = state.templates;
    // Ensure templates is always an array
    return Array.isArray(templates) ? templates : [];
  });
  const templatesLoading = useSelector((state) => state.templatesLoading === true);
  const templates = (templatesProp && Array.isArray(templatesProp)) 
    ? templatesProp 
    : (Array.isArray(reduxTemplates) ? reduxTemplates : []);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedTemplateForDetails, setSelectedTemplateForDetails] = useState(null);
  const [shouldShowMainModal, setShouldShowMainModal] = useState(isOpen);
  const [fieldSelectorTemplate, setFieldSelectorTemplate] = useState(null);
  const [targetDocumentIndex, setTargetDocumentIndex] = useState(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Use ref to store onSelect callback to avoid infinite loops
  const onSelectRef = useRef(onSelect);
  
  // Update ref when onSelect changes (without triggering Redux update)
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Sync shouldShowMainModal with isOpen prop
  useEffect(() => {
    setShouldShowMainModal(isOpen);
  }, [isOpen]);

  // Fetch templates from API whenever the modal is opened
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchTemplates());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && onSelectRef.current) {
      // Store the latest callback from ref
      dispatch(setTemplateSelectCallback(onSelectRef.current));
    } else if (!isOpen) {
      // Clear callback when modal closes
      dispatch(clearTemplateSelectCallback());
    }
  }, [isOpen, dispatch]);

  const sortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
  ];

  const filteredTemplates = templates.filter((template) => {
    // Validate template structure
    if (!template || typeof template !== 'object') {
      return false;
    }
    
    const searchLower = searchQuery.toLowerCase();
    
    // Search in title (with safety check)
    if (template.title && typeof template.title === 'string') {
      if (template.title.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    // Search in field values
    if (template.data && typeof template.data === 'object') {
      try {
        const fieldValues = Object.values(template.data)
          .filter(val => val !== null && val !== undefined)
          .map(val => {
            if (Array.isArray(val)) {
              return val.map(item => 
                typeof item === 'object' && item !== null ? Object.values(item).join(' ') : String(item)
              ).join(' ');
            }
            return String(val);
          })
          .join(' ')
          .toLowerCase();
        
        if (fieldValues.includes(searchLower)) {
          return true;
        }
      } catch (error) {
        console.warn('Error filtering template field values:', error);
      }
    }
    return false;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    // Safety checks for template structure
    const titleA = (a && typeof a === 'object' && a.title) ? String(a.title) : '';
    const titleB = (b && typeof b === 'object' && b.title) ? String(b.title) : '';
    
    switch (sortBy) {
      case 'name-asc':
        return titleA.localeCompare(titleB);
      case 'name-desc':
        return titleB.localeCompare(titleA);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || (isSpanish ? 'Ordenar por' : 'Sort by');
  };


  const openTemplateDetails = (template, e) => {
    e.stopPropagation(); // Prevent template selection when clicking "View details"
    setSelectedTemplateForDetails(template);
    // Close the main template modal to avoid overlay overlap
    setShouldShowMainModal(false);
    onClose();
  };

  const closeTemplateDetails = () => {
    setSelectedTemplateForDetails(null);
    // Reopen the main template modal when closing details
    setShouldShowMainModal(true);
  };


  // Create a new empty template
  const createNewTemplate = () => {
    const newTemplate = {
      id: generateNewTemplateId(),
      title: isSpanish ? 'Nueva Plantilla' : 'New Template',
      color: '#6B7280', // Default gray color
      data: {}
    };
    setSelectedTemplateForDetails(newTemplate);
    setShouldShowMainModal(false);
    setOpenInEditMode(true);
    onClose();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(fetchTemplates());
      toast.success(isSpanish ? 'Plantillas actualizadas' : 'Templates refreshed');
    } catch (error) {
      console.error('Error refreshing templates:', error);
      toast.error(isSpanish ? 'Error al actualizar plantillas' : 'Error refreshing templates');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      {/* Main Template Modal */}
      {shouldShowMainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 animate-modalEnter">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{title}</h2>
              <div className="flex items-center gap-2">
                {/* Add New Template Button */}
                <button
                  onClick={createNewTemplate}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 text-default rounded-full hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {isSpanish ? 'Nueva Plantilla' : 'New Template'}
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isSpanish ? 'Actualizar plantillas' : 'Refresh templates'}
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setIsSortOpen(false);
                    setShouldShowMainModal(false);
                    onClose();
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              {/* Search Input */}
              <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
                <div className="flex-0 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pr-4 py-2 !border-none no-border search-input text-sm"
                  style={{ borderWidth: '0px !important', border: '0px !important' }}
                  placeholder={isSpanish ? 'Buscar plantillas...' : 'Search templates...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Custom Sort Dropdown */}
              <div className="relative button-no-hover-effect text-xs flex">
                <button
                  className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full text-sm"
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span>{getSortLabel()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </button>

                {isSortOpen && (
                  <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          sortBy === option.value ? 'text-primary' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="h-[60vh] max-h-[60vh] overflow-y-auto" aria-busy={templatesLoading} aria-label={isSpanish ? 'Plantillas' : 'Templates'}>
              {templates.length > 0 ? (
                <div className="space-y-2">
                  {sortedTemplates.length > 0 ? (
                    sortedTemplates.map((template) => {
                      // Validate template structure
                      if (!template || typeof template !== 'object' || !template.id) {
                        return null; // Skip invalid templates
                      }
                      
                      // Safely organize template fields
                      let declarationFields = [];
                      let documentFields = [];
                      let itemFields = [];
                      let embalajesFields = [];
                      let hasFields = false;
                      try {
                        const organized = organizeTemplateFields(template);
                        declarationFields = organized?.declarationFields || [];
                        documentFields = organized?.documentFields || [];
                        itemFields = organized?.itemFields || [];
                        embalajesFields = organized?.embalajesFields || [];
                        hasFields = declarationFields.length > 0 || documentFields.length > 0 || itemFields.length > 0 || embalajesFields.length > 0;
                      } catch (error) {
                        console.warn('Error organizing template fields for display:', error);
                        hasFields = false;
                      }
                      
                      return (
                        <div
                          key={template.id}
                          className={`cursor-pointer border-bottom-gray`}
                          onClick={() => {
                            try {
                              if (template && typeof template === 'object' && onSelect && typeof onSelect === 'function') {
                                onSelect(template);
                                setSearchQuery('');
                                setIsSortOpen(false);
                                setShouldShowMainModal(false);
                                if (onClose && typeof onClose === 'function') {
                                  onClose();
                                }
                              }
                            } catch (error) {
                              console.error('Error selecting template:', error);
                            }
                          }}
                        >
                          <div className={`rounded-md hover:bg-gray-100 p-2 px-2 my-2  ${
                          selectedTemplate &&
                          selectedTemplate.id === template.id
                            ? 'bg-gray-100'
                            : ''
                        }`}>
                        <div className="flex items-start gap-0">
                          <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-4 h-4 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: template.color || '#CCCCCC' }}
                                  />
                                  <p className={`uppercase font-medium ${selectedTemplate && selectedTemplate.id === template.id ? 'font-bold' : ''}`}>
                                    {template.title || 'Untitled Template'}
                                  </p>
                                </div>
                                {hasFields && (
                                  <button
                                    onClick={(e) => openTemplateDetails(template, e)}
                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-200a rounded-fulla px-0 py-1"
                                  >
                                    <span>{isSpanish ? 'Ver detalles' : 'View details'}</span>
                                    <ChevronRight className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    );
                  })
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {isSpanish
                        ? `No se encontraron plantillas que coincidan con "${searchQuery}"`
                        : `No templates found matching "${searchQuery}"`}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="opacity-75 mb-4">
                    {isSpanish ? 'No hay plantillas disponibles' : 'No Templates Available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Details Modal - Renders independently */}
      {selectedTemplateForDetails && (
        <TemplateDetailsModal
          template={selectedTemplateForDetails}
          isOpen={!!selectedTemplateForDetails}
          onClose={() => {
            closeTemplateDetails();
            // If it was a new template that wasn't saved, don't reopen main modal
            if (selectedTemplateForDetails && selectedTemplateForDetails.id && typeof selectedTemplateForDetails.id === 'string' && selectedTemplateForDetails.id.startsWith('new-template-')) {
              setShouldShowMainModal(false);
            }
          }}
          isSpanish={isSpanish}
          formatFieldName={formatFieldName}
          organizeTemplateFields={organizeTemplateFields}
          openInEditMode={openInEditMode}
          onEditModeChange={(isEdit) => setOpenInEditMode(false)} // Reset flag after modal handles it
          onOpenFieldSelector={(template, documentIndex = null, originalTemplateForFiltering = null) => {
            // Attach original template to the template object for filtering
            const templateWithOriginal = originalTemplateForFiltering 
              ? { ...template, __originalForFiltering: originalTemplateForFiltering }
              : template;
            setFieldSelectorTemplate(templateWithOriginal);
            setTargetDocumentIndex(documentIndex);
            setSelectedTemplateForDetails(null);
            // Don't reopen main template modal when opening field selector
            setShouldShowMainModal(false);
          }}
          onTemplateSaved={(savedTemplate) => {
            // Update selectedTemplateForDetails with the saved template Use the savedTemplate directly since it's already the updated version
            setSelectedTemplateForDetails(savedTemplate);
          }}
          onDuplicate={(duplicatedTemplate) => {
            // set the duplicated template as the selected template for details
            setSelectedTemplateForDetails(duplicatedTemplate);
            setOpenInEditMode(true);
            closeTemplateDetails();
          }}
          onSelect={onSelect} 
        />
      )}

      {/* Field Selector Modal - Renders independently */}
      {fieldSelectorTemplate && (
        <FieldSelectorModal
          isOpen={!!fieldSelectorTemplate}
          template={fieldSelectorTemplate}
          targetDocumentIndex={targetDocumentIndex}
          onClose={(fieldWasSelected = false, updatedTemplateFromSelector = null) => {
            try {
              // Use the updated template passed from selector, or get from Redux, or fallback to original
              let updatedTemplate = fieldSelectorTemplate; // Default fallback
              
              if (updatedTemplateFromSelector && typeof updatedTemplateFromSelector === 'object') {
                updatedTemplate = updatedTemplateFromSelector;
              } else if (fieldSelectorTemplate && fieldSelectorTemplate.id && Array.isArray(reduxTemplates)) {
                const found = reduxTemplates.find(t => t && t.id === fieldSelectorTemplate.id);
                if (found) {
                  updatedTemplate = found;
                }
              }
              
              // Reopen the template details modal when closing field selector
              setSelectedTemplateForDetails(updatedTemplate);
              setFieldSelectorTemplate(null);
              setTargetDocumentIndex(null);
              // If a field was selected, open in edit mode
              if (fieldWasSelected) {
                setOpenInEditMode(true);
              }
            } catch (error) {
              console.error('Error handling field selector close:', error);
              setFieldSelectorTemplate(null);
              setTargetDocumentIndex(null);
            }
          }}
          isSpanish={isSpanish}
        />
      )}
    </>
  );
};


// Template Details Modal Component
const TemplateDetailsModal = ({
  template,
  isOpen,
  onClose,
  isSpanish,
  formatFieldName,
  organizeTemplateFields,
  onOpenFieldSelector,
  openInEditMode = false,
  onEditModeChange,
  onTemplateSaved,
  onDuplicate,
  onSelect = null, 
}) => {
  const dispatch = useDispatch();
  const reduxTemplates = useSelector((state) => {
    try {
      const templates = state?.templates;
      return Array.isArray(templates) ? templates : [];
    } catch (error) {
      console.error('Error accessing templates from Redux:', error);
      return [];
    }
  });
  const selectedTemplate = useSelector((state) => {
    try {
      return state?.documents?.selectedTemplate || null;
    } catch (error) {
      console.error('Error accessing selectedTemplate from Redux:', error);
      return null;
    }
  });
  
  const templateSelectCallback = onSelect;
  const [isEditMode, setIsEditMode] = useState(false);
  // Store original template data for proper reset on cancel
  const originalTemplateRef = useRef(null);
  const [editedTemplate, setEditedTemplate] = useState(() => {
    // Initialize with safe defaults and store original
    if (template && typeof template === 'object') {
      const original = {
        id: template.id || '',
        title: template.title || '',
        color: template.color || '#6B7280',
        data: template.data ? deepClone(template.data) : {},
      };
      originalTemplateRef.current = original;
      return original;
    }
    const empty = {
      id: '',
      title: '',
      color: '#6B7280',
      data: {},
    };
    originalTemplateRef.current = empty;
    return empty;
  });
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const colorPickerRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Use the color palette from utils with fallback
  const colorPalette = templateColorPalette || {};


  // Check if this is a new template (not yet saved) with safety check
  const isNewTemplate = template && 
    typeof template === 'object' && 
    template.id && 
    typeof template.id === 'string' && 
    template.id.startsWith('new-template-');

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle opening in edit mode
  useEffect(() => {
    if (openInEditMode) {
      // Store current template as original when entering edit mode
      if (editedTemplate && typeof editedTemplate === 'object') {
        originalTemplateRef.current = {
          id: editedTemplate.id || '',
          title: editedTemplate.title || '',
          color: editedTemplate.color || '#6B7280',
          data: editedTemplate.data ? deepClone(editedTemplate.data) : {},
        };
      }
      setIsEditMode(true);
      setIsMenuOpen(false);
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    }
  }, [openInEditMode, onEditModeChange, editedTemplate]);

  useEffect(() => {
    if (isEditMode) {
      setIsMenuOpen(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    // Safely update editedTemplate when template prop changes Only update if not in edit mode to avoid overwriting user's edits
    if (template && typeof template === 'object' && !isEditMode) {
      const updated = {
        id: template.id || '',
        title: template.title || '',
        color: template.color || '#6B7280',
        data: template.data ? deepClone(template.data) : {},
      };
      originalTemplateRef.current = updated; // Update original reference
      setEditedTemplate(updated);
    }
    // Reset edit mode flag if template changes (e.g., when reopening)
    if (openInEditMode && onEditModeChange) {
      // Reset the flag after handling it
      setTimeout(() => {
        if (onEditModeChange) {
          onEditModeChange(false);
        }
      }, 0);
    }
  }, [template, openInEditMode, onEditModeChange, isEditMode]);

  // Sync editedTemplate with template prop when it changes (e.g., after save)
  useEffect(() => {
    // Only sync when not in edit mode to avoid overwriting user's current edits
    if (!isEditMode && template && typeof template === 'object') {
      // Always update when template prop changes and we're not editing This ensures saved changes are reflected in the display
      const updated = {
        id: template.id || editedTemplate.id || '',
        title: template.title || editedTemplate.title || '',
        color: template.color || editedTemplate.color || '#6B7280',
        data: template.data ? deepClone(template.data) : (editedTemplate.data || {}),
      };
      originalTemplateRef.current = updated; // Update original reference
      setEditedTemplate(updated);
    }
  }, [template, isEditMode]);

  if (!isOpen) return null;

  // Safely organize template fields with fallback
  let declarationFields = [];
  let documentFields = [];
  let itemFields = [];
  let embalajesFields = [];
  try {
    const organized = organizeTemplateFields(editedTemplate);
    declarationFields = organized?.declarationFields || [];
    documentFields = organized?.documentFields || [];
    itemFields = organized?.itemFields || [];
    embalajesFields = organized?.embalajesFields || [];
  } catch (error) {
    console.error('Error organizing template fields:', error);
  }


  // Handle template title change
  const handleTitleChange = (newTitle) => {
    try {
      const title = typeof newTitle === 'string' ? newTitle : String(newTitle || '');
      setEditedTemplate(prev => ({
        ...prev,
        title: title
      }));
    } catch (error) {
      console.error('Error updating template title:', error);
    }
  };

  // Handle template color change
  const handleColorChange = (newColor) => {
    try {
      const color = typeof newColor === 'string' && newColor.match(/^#[0-9A-Fa-f]{6}$/) 
        ? newColor 
        : (newColor || '#6B7280');
      setEditedTemplate(prev => ({
        ...prev,
        color: color
      }));
    } catch (error) {
      console.error('Error updating template color:', error);
    }
  };

  const handleFieldChange = (fieldName, value, isDocument = false, docIndex = null, isItem = false, isEmbalaje = false, embalajeIndex = null) => {
    try {
      if (!fieldName || typeof fieldName !== 'string') {
        console.warn('Invalid field name:', fieldName);
        return;
      }

      const currentData = editedTemplate?.data || {};
      const newData = { ...currentData };
      
      if (isDocument && docIndex !== null && typeof docIndex === 'number') {
        if (!Array.isArray(newData.documentos)) {
          newData.documentos = [];
        }
        const newDocumentos = [...newData.documentos];
        if (!newDocumentos[docIndex]) {
          newDocumentos[docIndex] = {};
        }
        newDocumentos[docIndex] = { ...newDocumentos[docIndex], [fieldName]: value };
        newData.documentos = newDocumentos;
      } else if (isEmbalaje && embalajeIndex !== null && typeof embalajeIndex === 'number') {
        // Embalajes are stored in data.embalajes as an array of objects
        if (!Array.isArray(newData.embalajes)) {
          newData.embalajes = [];
        }
        const newEmbalajes = [...newData.embalajes];
        if (!newEmbalajes[embalajeIndex]) {
          newEmbalajes[embalajeIndex] = {};
        }
        newEmbalajes[embalajeIndex] = { ...newEmbalajes[embalajeIndex], [fieldName]: value };
        newData.embalajes = newEmbalajes;
      } else if (isItem) {
        // Item fields are stored in data.partidas as an object
        if (!newData.partidas || typeof newData.partidas !== 'object' || Array.isArray(newData.partidas)) {
          newData.partidas = {};
        }
        newData.partidas = { ...newData.partidas, [fieldName]: value };
      } else {
        // Declaration-level field
        newData[fieldName] = value;
      }
      
      setEditedTemplate(prev => ({
        ...prev,
        data: newData
      }));
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const handleRemoveField = (fieldName, isDocument = false, docIndex = null, isItem = false, isEmbalaje = false, embalajeIndex = null) => {
    try {
      if (!fieldName || typeof fieldName !== 'string') {
        console.warn('Invalid field name for removal:', fieldName);
        return;
      }

      const currentData = editedTemplate?.data || {};
      const newData = { ...currentData };
      
      if (isDocument && docIndex !== null && typeof docIndex === 'number') {
        if (Array.isArray(newData.documentos) && newData.documentos[docIndex]) {
          const newDocumentos = [...newData.documentos];
          const newDoc = { ...newDocumentos[docIndex] };
          delete newDoc[fieldName];
          if (Object.keys(newDoc).length === 0) {
            newDocumentos.splice(docIndex, 1);
          } else {
            newDocumentos[docIndex] = newDoc;
          }
          newData.documentos = newDocumentos.length > 0 ? newDocumentos : undefined;
        }
      } else if (isEmbalaje && embalajeIndex !== null && typeof embalajeIndex === 'number') {
        // Embalajes are stored in data.embalajes as an array of objects
        if (Array.isArray(newData.embalajes) && newData.embalajes[embalajeIndex]) {
          const newEmbalajes = [...newData.embalajes];
          const newEmbalaje = { ...newEmbalajes[embalajeIndex] };
          delete newEmbalaje[fieldName];
          if (Object.keys(newEmbalaje).length === 0) {
            newEmbalajes.splice(embalajeIndex, 1);
          } else {
            newEmbalajes[embalajeIndex] = newEmbalaje;
          }
          newData.embalajes = newEmbalajes.length > 0 ? newEmbalajes : undefined;
        }
      } else if (isItem) {
        // Item fields are stored in data.partidas as an object
        if (newData.partidas && typeof newData.partidas === 'object' && !Array.isArray(newData.partidas)) {
          const newPartidas = { ...newData.partidas };
          delete newPartidas[fieldName];
          if (Object.keys(newPartidas).length === 0) {
            delete newData.partidas;
          } else {
            newData.partidas = newPartidas;
          }
        }
      } else {
        // Declaration-level field
        delete newData[fieldName];
      }
      
      setEditedTemplate(prev => ({
        ...prev,
        data: newData
      }));
    } catch (error) {
      console.error('Error removing field:', error);
    }
  };

  const handleSave = async () => {
    try {
      // Validate template has a title
      const title = editedTemplate?.title;
      if (!title || (typeof title === 'string' && title.trim() === '')) {
        toast.error(isSpanish ? 'Por favor ingrese un nombre para la plantilla' : 'Please enter a template name');
        return;
      }

      // Validate editedTemplate structure
      if (!editedTemplate || typeof editedTemplate !== 'object') {
        console.error('Invalid editedTemplate structure');
        toast.error(isSpanish ? 'Error: Datos de plantilla inválidos' : 'Error: Invalid template data');
        return;
      }

      // Helper function to convert marcas strings to arrays recursively
      const convertMarcasToStrings = (data) => {
        if (!data || typeof data !== 'object') return data;
        const converted = { ...data };
        
        // Convert marcas in embalajes
        if (Array.isArray(converted.embalajes)) {
          converted.embalajes = converted.embalajes.map(embalaje => {
            if (embalaje && typeof embalaje === 'object') {
              const newEmbalaje = { ...embalaje };
              if (newEmbalaje.marcas && typeof newEmbalaje.marcas === 'string') {
                newEmbalaje.marcas = stringToMarcas(newEmbalaje.marcas);
              }
              return newEmbalaje;
            }
            return embalaje;
          });
        }
        
        return converted;
      };

      // Ensure color is included in the template to save Convert any string marcas values to arrays before saving
      const dataToSave = editedTemplate.data && typeof editedTemplate.data === 'object' 
        ? convertMarcasToStrings(editedTemplate.data)
        : {};
      
      const templateToSave = { 
        id: editedTemplate.id || '',
        title: typeof title === 'string' ? title.trim() : String(title || ''),
        color: editedTemplate.color || template?.color || '#6B7280', // Explicitly include color
        data: dataToSave,
      };

      // If it's a new template, generate a proper ID and check for duplicates
      if (isNewTemplate) {
        const baseId = generateTemplateId(templateToSave.title);
        let finalId = baseId;
        let counter = 1;
        
        // Check if ID already exists, if so append a number Add safety check to prevent infinite loop
        const maxAttempts = 1000;
        while (Array.isArray(reduxTemplates) && reduxTemplates.some(t => t && t.id === finalId) && counter < maxAttempts) {
          finalId = `${baseId}-${counter}`;
          counter++;
        }
        if (counter >= maxAttempts) {
          console.error('Could not generate unique template ID after max attempts');
          finalId = `${baseId}-${Date.now()}`;
        }
        
        const newTemplateToSave = {
          ...templateToSave,
          id: finalId,
        };
        
        const result = await dispatch(addTemplate(newTemplateToSave));
        
        if (result && result.success) {
          toast.success(isSpanish ? `Plantilla "${newTemplateToSave.title}" agregada exitosamente` : `Template "${newTemplateToSave.title}" added successfully`);
          
          // Fetch fresh templates from API after successful add
          dispatch(fetchTemplates());
          
          // Notify parent of saved template Merge API response with newTemplateToSave to ensure data field is preserved
          const savedNewTemplate = result.template
            ? {
                ...newTemplateToSave, // Start with what we sent (preserves data)
                ...result.template, // Override with API response
                data: result.template.data || newTemplateToSave.data || {}, // Ensure data field is preserved
              }
            : newTemplateToSave;
          
          if (onTemplateSaved && typeof onTemplateSaved === 'function') {
            onTemplateSaved(savedNewTemplate);
          }
          
          // Close modal after saving new template
          setIsEditMode(false);
          if (onClose && typeof onClose === 'function') {
            onClose();
          }
        } else {
          toast.error(isSpanish ? 'Error al agregar la plantilla. Por favor, intente nuevamente.' : 'Error adding template. Please try again.');
        }
      } else {
        // For existing templates, ensure we preserve all original fields
        const originalTemplate = template && typeof template === 'object' ? template : {};
        const completeTemplate = {
          ...originalTemplate, // Start with original template to preserve all fields
          ...templateToSave, // Override with edited values (including color)
          color: templateToSave.color || originalTemplate.color || '#6B7280', // Explicitly ensure color is set
        };
        
        // Validate template has an ID before updating
        if (!completeTemplate.id) {
          console.error('Cannot update template without ID');
          toast.error(isSpanish ? 'Error: La plantilla no tiene un ID válido' : 'Error: Template does not have a valid ID');
          return;
        }
        
        const result = await dispatch(updateTemplate(completeTemplate));
        
        if (result && result.success) {
          toast.success(isSpanish ? `Plantilla "${completeTemplate.title}" actualizada exitosamente` : `Template "${completeTemplate.title}" updated successfully`);
          
          // Fetch fresh templates from API after successful update
          dispatch(fetchTemplates());
          
          // Notify parent of saved template so it can update selectedTemplateForDetails Merge API response with completeTemplate to ensure data field is preserved
          const savedTemplate = result.template 
            ? {
                ...completeTemplate, // Start with what we sent (preserves data)
                ...result.template, // Override with API response
                data: result.template.data || completeTemplate.data || {}, // Ensure data field is preserved
              }
            : completeTemplate;
          
          // If this is the currently selected template, update Redux and re-apply it immediately
          const selectedTemplateId = selectedTemplate?.id ? String(selectedTemplate.id) : null;
          const savedTemplateId = completeTemplate?.id ? String(completeTemplate.id) : null;
          
          if (selectedTemplate && typeof selectedTemplate === 'object' && selectedTemplateId && savedTemplateId && selectedTemplateId === savedTemplateId) {
            dispatch(setSelectedTemplate(savedTemplate));
            if (templateSelectCallback && typeof templateSelectCallback === 'function') {
              try {
                templateSelectCallback(savedTemplate);
              } catch (error) {
                console.error('Error re-applying template after save:', error);
              }
            }
          }
          
          if (onTemplateSaved && typeof onTemplateSaved === 'function') {
            onTemplateSaved(savedTemplate);
          }
          
          setIsEditMode(false);
        } else {
          const errorMessage = result?.error || (isSpanish ? 'Error al actualizar la plantilla. Por favor, intente nuevamente.' : 'Error updating template. Please try again.');
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(isSpanish ? 'Error al guardar la plantilla. Por favor, intente nuevamente.' : 'Error saving template. Please try again.');
    }
  };

  const handleCancel = () => {
    try {
      // If it's a new template and user cancels, just close without saving
      if (isNewTemplate) {
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
        return;
      }
      
      // Reset to original template (from when edit mode was entered) Use the stored original template reference to ensure we remove any fields added during editing
      if (originalTemplateRef.current) {
        setEditedTemplate({
          id: originalTemplateRef.current.id || '',
          title: originalTemplateRef.current.title || '',
          color: originalTemplateRef.current.color || '#6B7280',
          data: originalTemplateRef.current.data ? deepClone(originalTemplateRef.current.data) : {},
        });
      } else if (template && typeof template === 'object') {
        // Fallback to template prop if ref is not available
        const reset = {
          id: template.id || '',
          title: template.title || '',
          color: template.color || '#6B7280',
          data: template.data ? deepClone(template.data) : {},
        };
        originalTemplateRef.current = reset;
        setEditedTemplate(reset);
      }
      setIsEditMode(false);
    } catch (error) {
      console.error('Error canceling template edit:', error);
      setIsEditMode(false);
    }
  };

  const handleDelete = async () => {
    try {
      // Don't allow deleting new templates that haven't been saved
      if (isNewTemplate) {
        return;
      }

      // Validate template has an ID
      if (!template || !template.id) {
        console.error('Cannot delete template without ID');
        toast.error(isSpanish ? 'Error: La plantilla no tiene un ID válido' : 'Error: Template does not have a valid ID');
        return;
      }

      // If this is the currently selected template, clear the selection
      if (selectedTemplate && selectedTemplate.id === template.id) {
        dispatch(clearSelectedTemplate());
      }

      // Delete the template
      const result = await dispatch(deleteTemplate(template.id));
      
      if (result && result.success) {
        toast.success(isSpanish ? `Plantilla "${template.title}" eliminada exitosamente` : `Template "${template.title}" deleted successfully`);
        
        // Fetch fresh templates from API after successful delete
        dispatch(fetchTemplates());
        
        // Close the modal
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      } else {
        const errorMessage = result?.error || (isSpanish ? 'Error al eliminar la plantilla. Por favor, intente nuevamente.' : 'Error deleting template. Please try again.');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(isSpanish ? 'Error al eliminar la plantilla. Por favor, intente nuevamente.' : 'Error deleting template. Please try again.');
    }
  };

  //generate a unique template title with number suffix
  const generateUniqueTemplateTitle = (baseTitle, existingTemplates) => {
    try {
      if (!baseTitle || typeof baseTitle !== 'string') {
        return baseTitle || (isSpanish ? 'Nueva Plantilla' : 'New Template');
      }

      const trimmedBase = baseTitle.trim();
      if (!trimmedBase) {
        return isSpanish ? 'Nueva Plantilla' : 'New Template';
      }

      const numberSuffixMatch = trimmedBase.match(/^(.+?)(?:\s+\((\d+)\))?$/);
      const base = numberSuffixMatch ? numberSuffixMatch[1].trim() : trimmedBase;
      
      if (!base) {
        return trimmedBase;
      }

      const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`^${escapedBase}(?:\\s+\\((\\d+)\\))?$`);
      
      const matchingTemplates = Array.isArray(existingTemplates)
        ? existingTemplates.filter(t => {
            if (!t || typeof t !== 'object' || !t.title || typeof t.title !== 'string') {
              return false;
            }
            return pattern.test(t.title.trim());
          })
        : [];

      let maxNumber = 0;
      matchingTemplates.forEach(t => {
        try {
          const match = t.title.trim().match(pattern);
          if (match && match[1]) {
            const num = parseInt(match[1], 10);
            if (!isNaN(num) && num > maxNumber) {
              maxNumber = num;
            }
          }
        } catch (err) {
          console.warn('Error parsing template number:', err);
        }
      });

      // Generate new title with next number
      return `${base} (${maxNumber + 1})`;
    } catch (error) {
      console.error('Error generating unique template title:', error);
      return baseTitle || (isSpanish ? 'Nueva Plantilla' : 'New Template');
    }
  };

  //generate a unique template ID
  const generateUniqueTemplateId = (title, existingTemplates) => {
    try {
      if (!title || typeof title !== 'string') {
        return generateNewTemplateId();
      }

      const baseId = generateTemplateId(title);
      let finalId = baseId;
      let counter = 1;
      const maxAttempts = 1000;

      while (
        Array.isArray(existingTemplates) &&
        existingTemplates.some(t => t && t.id === finalId) &&
        counter < maxAttempts
      ) {
        finalId = `${baseId}-${counter}`;
        counter++;
      }

      if (counter >= maxAttempts) {
        console.error('Could not generate unique template ID after max attempts');
        finalId = `${baseId}-${Date.now()}`;
      }

      return finalId;
    } catch (error) {
      console.error('Error generating unique template ID:', error);
      return generateNewTemplateId();
    }
  };

  //create a safe template copy for duplication
  const createTemplateCopy = (sourceTemplate, newTitle, newId) => {
    try {
      if (!sourceTemplate || typeof sourceTemplate !== 'object') {
        throw new Error('Invalid source template');
      }

      const safeTitle = typeof newTitle === 'string' && newTitle.trim()
        ? newTitle.trim()
        : (isSpanish ? 'Nueva Plantilla' : 'New Template');
      
      const safeColor = (typeof sourceTemplate.color === 'string' && sourceTemplate.color.match(/^#[0-9A-Fa-f]{6}$/))
        ? sourceTemplate.color
        : '#6B7280';

      const safeData = (sourceTemplate.data && typeof sourceTemplate.data === 'object')
        ? deepClone(sourceTemplate.data)
        : {};

      return {
        id: typeof newId === 'string' ? newId : generateNewTemplateId(),
        title: safeTitle,
        color: safeColor,
        data: safeData,
      };
    } catch (error) {
      console.error('Error creating template copy:', error);
      return {
        id: generateNewTemplateId(),
        title: isSpanish ? 'Nueva Plantilla' : 'New Template',
        color: '#6B7280',
        data: {},
      };
    }
  };

  const handleDuplicate = async () => {
    try {
      // don't allow duplicating new templates that haven't been saved
      if (isNewTemplate) {
        toast.error(isSpanish ? 'Por favor guarde la plantilla antes de duplicarla' : 'Please save the template before duplicating');
        return;
      }

      // validate template structure
      if (!template || typeof template !== 'object' || !template.id) {
        console.error('Cannot duplicate template: invalid template');
        toast.error(isSpanish ? 'Error: Plantilla inválida' : 'Error: Invalid template');
        return;
      }

      // validate template title
      const originalTitle = (template.title && typeof template.title === 'string')
        ? template.title
        : (isSpanish ? 'Nueva Plantilla' : 'New Template');

      // generate unique title with number suffix
      const newTitle = generateUniqueTemplateTitle(originalTitle, reduxTemplates);

      // generate unique ID
      const newId = generateUniqueTemplateId(newTitle, reduxTemplates);

      // create template copy
      const duplicatedTemplate = createTemplateCopy(template, newTitle, newId);

      // save to API
      const result = await dispatch(addTemplate(duplicatedTemplate));
      
      if (result && result.success) {
        toast.success(isSpanish ? `Plantilla "${newTitle}" duplicada exitosamente` : `Template "${newTitle}" duplicated successfully`);

        dispatch(fetchTemplates());
        
        // optionally open the duplicated template in edit mode
        if (onDuplicate && typeof onDuplicate === 'function') {
          const savedTemplate = result.template
            ? {
                ...duplicatedTemplate,
                ...result.template,
                data: (result.template.data && typeof result.template.data === 'object')
                  ? result.template.data
                  : (duplicatedTemplate.data || {}),
              }
            : duplicatedTemplate;
          onDuplicate(savedTemplate);
        }
      } else {
        const errorMessage = result?.error || (isSpanish ? 'Error al duplicar la plantilla. Por favor, intente nuevamente.' : 'Error duplicating template. Please try again.');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error(isSpanish ? 'Error al duplicar la plantilla. Por favor, intente nuevamente.' : 'Error duplicating template. Please try again.');
    }
  };

  // Render field card component
  const renderFieldCard = (field, isDocument = false, docIndex = null, isItem = false, isEmbalaje = false, embalajeIndex = null) => {
    try {
      // Validate field structure
      if (!field || typeof field !== 'object' || !field.name) {
        console.warn('Invalid field structure:', field);
        return null;
      }

      const fieldName = field.name || '';
      const fieldValue = field.value;
      // Check if value is an array (like embalajes)
      const isArrayValue = Array.isArray(fieldValue);
      const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && (!isArrayValue || fieldValue.length > 0);
      let bgColor = '';
      if (!hasValue) {
        bgColor = 'bg-red-50';
      } else {
        bgColor = 'bg-gray-100';
      }
      
      // Safely format field name
      let formattedName = fieldName;
      try {
        if (formatFieldName && typeof formatFieldName === 'function') {
          formattedName = formatFieldName(fieldName);
        } else {
          formattedName = fieldName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      } catch (error) {
        console.warn('Error formatting field name:', error);
        formattedName = fieldName;
      }
      
      return (
        <div 
          key={fieldName}
          className={`text-sm break-words ${bgColor}a py-1 rounded-full relative`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 flex items-end gap-2">
              <span className={`font-semibold break-words text-gray-700`}>
                {formattedName}
              </span>
              <div className="text-[10px] mb-[-2px] text-gray-500 font-mono break-all">
                {fieldName}
              </div>
            </div>
            {isEditMode && (
              <button
                onClick={() => handleRemoveField(fieldName, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex)}
                className="text-red-500 hover:text-red-700 text-xs pl-2"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {isEditMode ? (
            <input
              type="text"
              value={hasValue 
                ? (fieldName === 'marcas' 
                    ? (isArrayValue 
                        ? marcasToString(fieldValue) 
                        : (typeof fieldValue === 'string' ? fieldValue : String(fieldValue || ''))
                      )
                    : (isArrayValue ? JSON.stringify(fieldValue) : String(fieldValue))
                  )
                : ''}
              onChange={(e) => {
                let newValue = e.target.value;
                // For marcas field: store raw string during typing, convert to array on blur
                if (fieldName === 'marcas') {
                  // Store the raw string value directly - don't convert to array yet This allows commas to be typed freely
                  handleFieldChange(fieldName, newValue, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                } else if (isArrayValue && fieldName !== 'marcas') {
                  // Try to parse as JSON for other array fields
                  try {
                    const parsed = JSON.parse(newValue);
                    if (Array.isArray(parsed)) {
                      handleFieldChange(fieldName, parsed, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                    } else {
                      handleFieldChange(fieldName, newValue, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                    }
                  } catch {
                    // If JSON parsing fails, store as string (user can fix it)
                    handleFieldChange(fieldName, newValue, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                  }
                } else {
                  handleFieldChange(fieldName, newValue, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                }
              }}
              onBlur={(e) => {
                const newValue = e.target.value;
                // Convert marcas string to array when field loses focus
                if (fieldName === 'marcas') {
                  const marcasArray = stringToMarcas(newValue);
                  handleFieldChange(fieldName, marcasArray, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                } else {
                  // Convert numeric fields to numbers when field loses focus
                  const convertedValue = convertFieldValue(fieldName, newValue);
                  handleFieldChange(fieldName, convertedValue, isDocument, docIndex, isItem, isEmbalaje, embalajeIndex);
                }
              }}
              className="text-xs mt-1 w-full py-1 border border-gray-300 rounded"
              placeholder={fieldName === 'marcas'
                ? (isSpanish ? 'Ingrese marcas separadas por comas, ej: RTDAS, marca2' : 'Enter marks separated by commas, e.g: RTDAS, mark2')
                : (isArrayValue && fieldName !== 'marcas'
                    ? (isSpanish ? 'Ingrese JSON array, ej: []' : 'Enter JSON array, e.g: []')
                    : (isSpanish ? 'Ingrese valor' : 'Enter value')
                  )}
            />
          ) : (
            <div className={`text-xs mt-1 break-words ${hasValue ? 'text-gray-600' : 'text-red-500'}`}>
              {hasValue 
                ? (fieldName === 'marcas' && isArrayValue
                    ? marcasToString(fieldValue)
                    : (isArrayValue 
                        ? `[${fieldValue.length} ${isSpanish ? 'elementos' : 'items'}] ${JSON.stringify(fieldValue)}`
                        : (typeof fieldValue === 'object' ? JSON.stringify(fieldValue) : String(fieldValue))
                      )
                  )
                : (isSpanish ? 'Sin valor' : 'No value')
              }
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Error rendering field card:', error);
      return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-999">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] h-[80vh] flex flex-col animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isSpanish ? 'Detalles de Plantilla' : 'Template Details'}</h2>
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  title={isSpanish ? 'Más opciones' : 'More options'}
                >
                  <Ellipsis className="w-5 h-5" />
                </button>
                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-50">
                    <button
                      onClick={() => {
                        // store current template as original when entering edit mode
                        if (editedTemplate && typeof editedTemplate === 'object') {
                          originalTemplateRef.current = {
                            id: editedTemplate.id || '',
                            title: editedTemplate.title || '',
                            color: editedTemplate.color || '#6B7280',
                            data: editedTemplate.data ? deepClone(editedTemplate.data) : {},
                          };
                        }
                        setIsEditMode(true);
                        setIsMenuOpen(false);
                      }}
                      className="button-no-hover-effect w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      {isSpanish ? 'Editar' : 'Edit'}
                    </button>
                    {!isNewTemplate && (
                      <>
                        <button
                          onClick={() => {
                            handleDuplicate();
                            setIsMenuOpen(false);
                          }}
                          className="button-no-hover-effect w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          {isSpanish ? 'Duplicar' : 'Duplicate'}
                        </button>
                        <button
                          onClick={() => {
                            handleDelete();
                            setIsMenuOpen(false);
                          }}
                          className="button-no-hover-effect w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          {isSpanish ? 'Eliminar' : 'Delete'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-default text-white rounded-full"
                >
                  <Save className="w-4 h-4 stroke-white" />
                  {isSpanish ? 'Guardar' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm bg-gray-100 text-default rounded-full"
                >
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Color and Title Section */}
        <div className="mb-0 pb-4">
          {isEditMode ? (
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-2 relative" ref={colorPickerRef}>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                    className="w-10 h-10 rounded-full  cursor-pointer  transition-all shadow-sm"
                    style={{ backgroundColor: editedTemplate.color || '#6B7280' }}
                    title={isSpanish ? 'Color de plantilla' : 'Template color'}
                  />
                  
                  {isColorPickerOpen && (
                    <div className="absolute top-12 left-0 z-50 bg-white rounded-lg shadow-lg border-container   p-3 min-w-[200px] w-fit">
                      <label className="text-xs font-medium text-gray-700 mb-2 block">
                        {isSpanish ? 'Colores predefinidos' : 'Predefined Colors'}
                      </label>
                      <div className="grid grid-cols-6 gap-2 w-max">
                        {Object.keys(colorPalette || {}).map((primaryColor) => {
                          if (!primaryColor || typeof primaryColor !== 'string') return null;
                          return (
                            <button
                              key={primaryColor}
                              type="button"
                              onClick={() => {
                                try {
                                  handleColorChange(primaryColor);
                                  setIsColorPickerOpen(false);
                                } catch (error) {
                                  console.error('Error selecting color:', error);
                                }
                              }}
                              className={`w-8 h-8 rounded-full transition-all ${
                                editedTemplate?.color === primaryColor
                                  ? ''
                                  : ''
                              }`}
                              style={{ backgroundColor: primaryColor }}
                              title={primaryColor}
                            >
                              {editedTemplate?.color === primaryColor && (
                                <Check className="w-4 h-4 text-white m-auto drop-shadow-lg" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <input
                  type="text"
                  value={editedTemplate.title || ''}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="text-lg font-bold uppercase flex-1 py-2 border border-gray-300 rounded"
                  placeholder={isSpanish ? 'Nombre de plantilla' : 'Template name'}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-2">
                <div
                  className="w-10 h-10 rounded-full flex-shrink-0 "
                  style={{ backgroundColor: editedTemplate.color || '#6B7280' }}
                />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <h3 className="text-lg font-bold uppercase">{editedTemplate.title || (isSpanish ? 'Nueva Plantilla' : 'New Template')}</h3>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/* Add Field Button - Always show in edit mode */}
            {isEditMode && (
              <div className="mb-4 pt-2 flex gap-2">
                <button
                  onClick={() => {
                    // For existing templates, save current edits before opening field selector For new templates, just pass the current edited template
                    if (!isNewTemplate) {
                      dispatch(updateTemplate(editedTemplate));
                    }
                    // Pass original template for filtering (to avoid filtering out fields added during editing)
                    const originalForFiltering = originalTemplateRef.current || template;
                    onOpenFieldSelector(editedTemplate, null, originalForFiltering);
                  }}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-secondary text-default rounded-full"
                >
                  <Plus className="w-4 h-4" />
                  {isSpanish ? 'Agregar Campo' : 'Add Field'}
                </button>
              </div>
            )}

            {/* Declaration Fields */}
            {declarationFields.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-700">
                    {!isSpanish ? "Declaration Fields" : "Campos de Declaración"}
                  </h3>
                </div>
                <div className="space-y-2">
                  {Array.isArray(declarationFields) && declarationFields.length > 0
                    ? declarationFields.map((field, index) => {
                        try {
                          const rendered = renderFieldCard(field, false, null);
                          return rendered ? <div key={field?.name || `declaration-${index}`}>{rendered}</div> : null;
                        } catch (error) {
                          console.error('Error rendering declaration field:', error);
                          return null;
                        }
                      })
                    : null}
                </div>
              </div>
            )}
            
            {/* Item Fields */}
            {itemFields.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-700">
                    {!isSpanish ? "Item Fields" : "Campos de Partida"}
                  </h3>
                </div>
                <div className="space-y-2">
                  {Array.isArray(itemFields) && itemFields.length > 0
                    ? itemFields.map((field, index) => {
                        try {
                          const rendered = renderFieldCard(field, false, null, true);
                          return rendered ? <div key={field?.name || `item-${index}`}>{rendered}</div> : null;
                        } catch (error) {
                          console.error('Error rendering item field:', error);
                          return null;
                        }
                      })
                    : null}
                </div>
              </div>
            )}
            
            {/* Embalajes Fields */}
            {embalajesFields.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-700">
                    {!isSpanish ? "Package Fields" : "Campos de Embalaje"}
                  </h3>
                </div>
                <div className="space-y-2">
                  {Array.isArray(embalajesFields) && embalajesFields.length > 0
                    ? embalajesFields.map((field, index) => {
                        try {
                          // For embalajes, we use embalajeIndex 0 since we're just storing fields to apply
                          const rendered = renderFieldCard(field, false, null, false, true, 0);
                          return rendered ? <div key={field?.name || `embalaje-${index}`}>{rendered}</div> : null;
                        } catch (error) {
                          console.error('Error rendering embalaje field:', error);
                          return null;
                        }
                      })
                    : null}
                </div>
              </div>
            )}
            
            {/* Document Fields */}
            {(documentFields.length > 0 || (isEditMode && editedTemplate?.data?.documentos && Array.isArray(editedTemplate.data.documentos) && editedTemplate.data.documentos.length > 0)) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-700">
                    {isSpanish ? "Campos de Documento" : "Documents Fields"}
                  </h3>
                  {isEditMode && (
                    <button
                      onClick={() => {
                        // Save current edits before opening field selector for documents
                        if (!isNewTemplate) {
                          dispatch(updateTemplate(editedTemplate));
                        }
                        // Open field selector - when document fields are selected, they will create a new documento Pass original template for filtering (to avoid filtering out fields added during editing)
                        const originalForFiltering = originalTemplateRef.current || template;
                        onOpenFieldSelector(editedTemplate, null, originalForFiltering);
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-secondary text-default rounded-full"
                    >
                      <Plus className="w-3 h-3" />
                      {isSpanish ? 'Agregar Documento' : 'Add Document'}
                    </button>
                  )}
                </div>
                {Array.isArray(documentFields) && documentFields.length > 0
                  ? documentFields.map((doc, docIdx) => {
                      try {
                        if (!doc || typeof doc !== 'object' || !Array.isArray(doc.fields)) {
                          return null;
                        }
                        const docIndex = typeof doc.docIndex === 'number' ? doc.docIndex : docIdx;
                        return (
                          <div key={docIdx} className="ml-4 mb-2">
                            <p className="text-xs font-medium mb-1 text-gray-500">
                              {!isSpanish ? "Document" : "Documento"} #{docIndex + 1}
                            </p>
                            <div className="space-y-2">
                              {doc.fields.length > 0
                                ? doc.fields.map((field, fieldIndex) => {
                                    try {
                                      const rendered = renderFieldCard(field, true, docIndex);
                                      return rendered ? <div key={field?.name || `doc-${docIndex}-field-${fieldIndex}`}>{rendered}</div> : null;
                                    } catch (error) {
                                      console.error('Error rendering document field:', error);
                                      return null;
                                    }
                                  })
                                : null}
                              {isEditMode && (
                                <button
                                  onClick={() => {
                                    // Save current edits before opening field selector
                                    if (!isNewTemplate) {
                                      dispatch(updateTemplate(editedTemplate));
                                    }
                                    // Open field selector for this specific documento Pass original template for filtering (to avoid filtering out fields added during editing)
                                    const originalForFiltering = originalTemplateRef.current || template;
                                    onOpenFieldSelector(editedTemplate, docIndex, originalForFiltering);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-secondary text-default rounded-full mt-2"
                                >
                                  <Plus className="w-3 h-3" />
                                  {isSpanish ? 'Agregar Campo' : 'Add Field'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      } catch (error) {
                        console.error('Error rendering document:', error);
                        return null;
                      }
                    })
                  : null}
              </div>
            )}

            {declarationFields.length === 0 && itemFields.length === 0 && embalajesFields.length === 0 && documentFields.length === 0 && !isEditMode && (
              <p className="text-gray-500 text-sm text-center py-4">
                {isSpanish ? 'No hay campos disponibles' : 'No fields available'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Field Selector Modal Component
const FieldSelectorModal = ({
  isOpen,
  onClose,
  template,
  targetDocumentIndex = null,
  isSpanish,
}) => {
  const dispatch = useDispatch();
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [selectedFieldLevel, setSelectedFieldLevel] = useState('declaration');
  const [selectedFields, setSelectedFields] = useState([]); // Track selected fields: [{fieldName, fieldLevel}]

  if (!isOpen || !template || typeof template !== 'object') return null;

  // CRITICAL: Use original template for filtering existing fields (not the edited template) This allows adding fields that were added during editing but not yet saved
  const templateForFiltering = template.__originalForFiltering || template;
  
  // Use document-specific fields if targetDocumentIndex is provided When targetDocumentIndex is null, we're creating a new documento, so don't filter any document fields
  const allExistingFields = getExistingFields(templateForFiltering);
  const documentSpecificFields = targetDocumentIndex !== null && targetDocumentIndex !== undefined 
    ? getExistingFieldsForDocument(templateForFiltering, targetDocumentIndex)
    : null;
  
  const existingDeclarationFields = allExistingFields.existingDeclarationFields;
  // Only filter document fields if we're adding to a specific existing documento When creating a new documento (targetDocumentIndex is null), show all document fields
  const existingDocumentFields = (targetDocumentIndex !== null && targetDocumentIndex !== undefined && documentSpecificFields)
    ? documentSpecificFields.existingDocumentFields 
    : new Set(); // Empty set means no filtering - all fields available for new documentos
  const existingItemFields = allExistingFields.existingItemFields;
  const existingEmbalajeFields = allExistingFields.existingEmbalajeFields;
  
  // Safely get available fields
  let availableFields = {};
  try {
    availableFields = getAvailableFields(isSpanish) || {};
  } catch (error) {
    console.error('Error getting available fields:', error);
    availableFields = {};
  }

  // Safely filter fields
  let filteredFields = [];
  try {
    const fieldsForLevel = availableFields[selectedFieldLevel];
    if (Array.isArray(fieldsForLevel)) {
      filteredFields = fieldsForLevel.filter(field => {
        if (!field || typeof field !== 'object' || !field.name) {
          return false;
        }
        
        // Filter by search query
        const searchLower = fieldSearchQuery.toLowerCase();
        const matchesSearch = 
          (field.name && typeof field.name === 'string' && field.name.toLowerCase().includes(searchLower)) ||
          (field.label && typeof field.label === 'string' && field.label.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
        
        // Filter out existing fields
        if (selectedFieldLevel === 'declaration') {
          return !existingDeclarationFields.has(field.name);
        } else if (selectedFieldLevel === 'document') {
          return !existingDocumentFields.has(field.name);
        } else if (selectedFieldLevel === 'item') {
          return !existingItemFields.has(field.name);
        } else if (selectedFieldLevel === 'embalaje') {
          return !existingEmbalajeFields.has(field.name);
        }
        
        return true;
      });
    }
  } catch (error) {
    console.error('Error filtering fields:', error);
    filteredFields = [];
  }

  // Toggle field selection (add/remove from selectedFields)
  const handleToggleFieldSelection = (fieldName, fieldLevel) => {
    setSelectedFields(prev => {
      const fieldKey = `${fieldLevel}:${fieldName}`;
      const isSelected = prev.some(f => `${f.fieldLevel}:${f.fieldName}` === fieldKey);
      
      if (isSelected) {
        // Remove from selection
        return prev.filter(f => `${f.fieldLevel}:${f.fieldName}` !== fieldKey);
      } else {
        // Add to selection
        return [...prev, { fieldName, fieldLevel }];
      }
    });
  };

  // Check if a field is selected
  const isFieldSelected = (fieldName, fieldLevel) => {
    const fieldKey = `${fieldLevel}:${fieldName}`;
    return selectedFields.some(f => `${f.fieldLevel}:${f.fieldName}` === fieldKey);
  };

  // Confirm and add all selected fields at once
  const handleConfirmSelectedFields = () => {
    if (selectedFields.length === 0) {
      return; // Nothing to add
    }

    // Use the latest template data - ensure we're working with the most current state
    const currentTemplateData = template?.data || {};
    const newData = addFieldsToTemplateData(currentTemplateData, selectedFields, targetDocumentIndex);
    const updatedTemplate = { ...template, data: newData };
    
    // Only dispatch updateTemplate if it's an existing template (not a new one)
    const isNewTemplate = template && template.id && typeof template.id === 'string' && template.id.startsWith('new-template-');
    if (!isNewTemplate) {
      dispatch(updateTemplate(updatedTemplate));
    }
    
    // Reset selected fields and close with flag and pass the updated template directly
    setSelectedFields([]);
    onClose(true, updatedTemplate);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-999">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {isSpanish ? 'Seleccionar Campo' : 'Select Field'}
          </h2>
          <div className="flex items-center gap-2">
            {selectedFields.length > 0 && (
          <button
                onClick={handleConfirmSelectedFields}
                className="px-4 py-2 bg-default text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <Check className="w-4 h-4 stroke-white" />
                {isSpanish ? `Confirmar ${selectedFields.length} seleccionado${selectedFields.length > 1 ? 's' : ''}` : `Confirm ${selectedFields.length} Selected`}
              </button>
            )}
            <button
              onClick={() => {
                setSelectedFields([]); // Clear selections when closing
                onClose();
              }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSelectedFieldLevel('declaration')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'declaration'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Declaración' : 'Declaration'}
          </button>
          <button
            onClick={() => setSelectedFieldLevel('item')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'item'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Partida' : 'Item'}
          </button>
          <button
            onClick={() => setSelectedFieldLevel('embalaje')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'embalaje'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Embalaje' : 'Package'}
          </button>
          <button
            onClick={() => setSelectedFieldLevel('document')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'document'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Documento' : 'Document'}
          </button>
        </div>

        <div className="relative mb-4 border-container rounded-full px-3 py-1 flex-1 flex-row">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={fieldSearchQuery}
            onChange={(e) => setFieldSearchQuery(e.target.value)}
            placeholder={isSpanish ? 'Buscar campo...' : 'Search field...'}
            className="w-full pl-7 pr-4 py-2 !border-none no-border search-input text-sm focus:outline-none"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2">
            {Array.isArray(filteredFields) && filteredFields.length > 0 ? (
              filteredFields.map((field, index) => {
                if (!field || typeof field !== 'object' || !field.name) {
                  return null;
                }
                const isSelected = isFieldSelected(field.name, selectedFieldLevel);
                return (
                  <button
                    key={field.name || `field-${index}`}
                    onClick={() => handleToggleFieldSelection(field.name, selectedFieldLevel)}
                    className={`w-full text-left px-5 py-4 rounded-full hover-scale-none flex justify-between items-center transition-all ${
                      isSelected 
                        ? 'bg-secondary text-default' 
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isSelected ? 'text-default' : 'text-gray-900'}`}>
                        {field.label || field.name}
                      </span>
                      <span className={`text-[8px] font-mono mt-1 ${isSelected ? 'text-default/80' : 'text-gray-500'}`}>
                        {field.name}
                      </span>
                    </div>
                    {isSelected ? (
                      <Check className="w-4 h-4 text-default flex-shrink-0" />
                    ) : (
                    <Plus className="w-4 h-4 text-secondary flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">
                  {fieldSearchQuery
                    ? (isSpanish ? `No se encontraron campos que coincidan con "${fieldSearchQuery}"` : `No fields found matching "${fieldSearchQuery}"`)
                    : (isSpanish ? 'No se encontraron campos disponibles' : 'No fields available')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

